import React from 'react';
import {
  Box,
  Typography,
  Chip,
  CircularProgress,
  Alert,
  Card,
  CardContent,
  Stack,
  Divider
} from '@mui/material';
import {
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon,
  Edit as EditIcon,
  Send as SendIcon,
  Schedule as ScheduleIcon,
  Circle as CircleIcon
} from '@mui/icons-material';
import { format } from 'date-fns';
import { fr, enUS } from 'date-fns/locale';
import { useTranslation } from 'react-i18next';
import { ProposalWorkflowAction, FreelancerProposalStatus } from '../../models/ProposalWorkflow';
import { useProposalStatusHistory } from '../../hooks/useProposalWorkflow';
import { zoullooColors } from '../../theme';

interface ProposalStatusHistoryProps {
  proposalId: number;
}

const ProposalStatusHistory: React.FC<ProposalStatusHistoryProps> = ({ proposalId }) => {
  const { t, i18n } = useTranslation();
  const { data: history, isLoading, error } = useProposalStatusHistory(proposalId);

  const getActionIcon = (action: ProposalWorkflowAction) => {
    switch (action) {
      case ProposalWorkflowAction.Accept:
        return <CheckCircleIcon />;
      case ProposalWorkflowAction.RefuseByClient:
      case ProposalWorkflowAction.RefuseByTalent:
      case ProposalWorkflowAction.Cancel:
        return <CancelIcon />;
      case ProposalWorkflowAction.RequestRevision:
      case ProposalWorkflowAction.Revise:
        return <EditIcon />;
      case ProposalWorkflowAction.Submit:
      case ProposalWorkflowAction.TransmitToClient:
        return <SendIcon />;
      case ProposalWorkflowAction.ScheduleCasting:
      case ProposalWorkflowAction.RequestCasting:
        return <ScheduleIcon />;
      default:
        return <CircleIcon />;
    }
  };

  const getActionColor = (action: ProposalWorkflowAction): string => {
    switch (action) {
      case ProposalWorkflowAction.Accept:
        return zoullooColors.success.main; // Vert
      case ProposalWorkflowAction.RefuseByClient:
      case ProposalWorkflowAction.RefuseByTalent:
      case ProposalWorkflowAction.Cancel:
        return zoullooColors.error.main; // Rouge
      case ProposalWorkflowAction.Submit:
      case ProposalWorkflowAction.TransmitToClient:
        return zoullooColors.secondary.main; // Persian Blue from Figma
      case ProposalWorkflowAction.RequestRevision:
      case ProposalWorkflowAction.Revise:
        return zoullooColors.primary.main; // Coquelicot (Orange) from Figma
      case ProposalWorkflowAction.ScheduleCasting:
      case ProposalWorkflowAction.RequestCasting:
        return zoullooColors.info.main; // Bleu
      default:
        return zoullooColors.grey[450]; // Gris moyen from Figma
    }
  };

  const getStatusChipColor = (status: FreelancerProposalStatus): string => {
    switch (status) {
      case FreelancerProposalStatus.NotSubmitted:
        return zoullooColors.grey[450]; // Gris moyen
      case FreelancerProposalStatus.SubmittedCSM:
        return zoullooColors.secondary.main; // Persian Blue #4836bf
      case FreelancerProposalStatus.SubmittedClient:
        return zoullooColors.secondary.dark; // Midnight Blue #201E69
      case FreelancerProposalStatus.InDiscussionWithCSM:
        return zoullooColors.primary.main; // Coquelicot #FF6300
      case FreelancerProposalStatus.InDiscussionWithClient:
        return zoullooColors.primary.light; // Orange clair #FF8F70
      case FreelancerProposalStatus.RevisedForCSM:
        return zoullooColors.proposal.gunmetal; // Bleu-gris #242C35
      case FreelancerProposalStatus.RevisedForClient:
        return zoullooColors.proposal.dyke; // Brun #3B2325
      case FreelancerProposalStatus.CastingToSchedule:
        return '#6B5CE5'; // Violet (nuance du Persian Blue)
      case FreelancerProposalStatus.CastingScheduled:
        return '#5B4AC9'; // Violet plus foncé
      case FreelancerProposalStatus.Accepted:
        return zoullooColors.success.main; // Vert #4CAF50
      case FreelancerProposalStatus.RefusedTalent:
        return zoullooColors.error.main; // Rouge #F44336
      case FreelancerProposalStatus.RefusedClient:
        return zoullooColors.error.dark; // Rouge foncé #D32F2F
      case FreelancerProposalStatus.Cancelled:
        return zoullooColors.grey[900]; // Gris foncé #2A2A2A
      default:
        return zoullooColors.grey[450];
    }
  };

  if (isLoading) {
    return (
      <Box display="flex" justifyContent="center" p={3}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Alert severity="error" sx={{ m: 2 }}>
        {t('proposal.historyLoadError')}
      </Alert>
    );
  }

  if (!history || history.length === 0) {
    return (
      <Alert severity="info" sx={{ m: 2 }}>
        {t('proposal.noHistory')}
      </Alert>
    );
  }

  const dateLocale = i18n.language === 'fr' ? fr : enUS;

  // Get the appropriate action label, handling special cases
  const getActionLabel = (action: ProposalWorkflowAction, fromStatus: FreelancerProposalStatus): string => {
    // For Submit action, distinguish between first submission and resubmission
    if (action === ProposalWorkflowAction.Submit) {
      if (fromStatus === FreelancerProposalStatus.NotSubmitted) {
        return t('proposal.actions.SubmitFirst');
      }
    }
    return t(`proposal.actions.${action}`);
  };

  return (
    <Box>
      <Typography variant="h6" gutterBottom sx={{ mb: 3 }}>
        {t('proposal.statusHistory')}
      </Typography>

      <Stack spacing={2}>
        {history.map((item, index) => (
          <Box key={item.id} display="flex" gap={2}>
            {/* Timeline Line and Dot */}
            <Box display="flex" flexDirection="column" alignItems="center">
              <Box
                sx={{
                  width: 40,
                  height: 40,
                  borderRadius: '50%',
                  backgroundColor: getActionColor(item.action),
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: 'white'
                }}
              >
                {React.cloneElement(getActionIcon(item.action), { fontSize: 'small' })}
              </Box>
              {index < history.length - 1 && (
                <Box
                  sx={{
                    width: 2,
                    flexGrow: 1,
                    backgroundColor: zoullooColors.grey[200], // Gris from Figma
                    mt: 1,
                    mb: 1
                  }}
                />
              )}
            </Box>

            {/* Content Card */}
            <Card sx={{ flex: 1, mb: index < history.length - 1 ? 2 : 0 }}>
              <CardContent>
                <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
                  <Typography variant="subtitle1" fontWeight="bold">
                    {getActionLabel(item.action, item.fromStatus)}
                  </Typography>
                  <Typography variant="caption" color="textSecondary">
                    {format(new Date(item.changedAt), 'dd MMM yyyy HH:mm', { locale: dateLocale })}
                  </Typography>
                </Box>

                <Box display="flex" gap={1} alignItems="center" mb={1}>
                  <Chip
                    size="small"
                    label={t(`proposal.status.${item.fromStatus}`)}
                    variant="outlined"
                    sx={{
                      borderColor: getStatusChipColor(item.fromStatus),
                      color: zoullooColors.text.primary,
                      fontWeight: 500
                    }}
                  />
                  <Typography variant="caption">→</Typography>
                  <Chip
                    size="small"
                    label={t(`proposal.status.${item.toStatus}`)}
                    variant="outlined"
                    sx={{
                      borderColor: getStatusChipColor(item.toStatus),
                      color: zoullooColors.text.primary,
                      fontWeight: 500
                    }}
                  />
                </Box>

                {item.changedByUserName && (
                  <Typography variant="caption" color="textSecondary">
                    {t('proposal.byUser', { user: item.changedByUserName })}
                  </Typography>
                )}

                {item.reason && (
                  <Box mt={1}>
                    <Divider sx={{ my: 1 }} />
                    <Typography variant="caption" color="textSecondary">
                      <strong>{t('proposal.reason')}:</strong> {item.reason}
                    </Typography>
                  </Box>
                )}

                {item.revisionRequestMessage && (
                  <Box mt={1}>
                    <Divider sx={{ my: 1 }} />
                    <Typography variant="caption" color="textSecondary">
                      <strong>{t('proposal.revisionRequest')}:</strong> {item.revisionRequestMessage}
                    </Typography>
                  </Box>
                )}

                {item.castingProposedSlots && (
                  <Box mt={1}>
                    <Divider sx={{ my: 1 }} />
                    <Typography variant="caption" color="textSecondary">
                      <strong>{t('proposal.castingSlots')}:</strong> {item.castingProposedSlots}
                    </Typography>
                  </Box>
                )}

                {item.hasContentChanges && (
                  <Box mt={1}>
                    <Chip
                      size="small"
                      label={t('proposal.hasContentChanges')}
                      color="info"
                      variant="outlined"
                    />
                  </Box>
                )}
              </CardContent>
            </Card>
          </Box>
        ))}
      </Stack>
    </Box>
  );
};

export default ProposalStatusHistory;